import { useState, useEffect } from 'react';
import { X, Plus, Pencil, Trash2, Loader2, AlertCircle, RefreshCw, Hash, ChevronDown, ChevronRight } from 'lucide-react';
import { customerTypesService, CustomerType, CreateCustomerTypeDto } from '../../services/customerTypes.service';
import { subscriptionPlansService, SubscriptionPlan, CreateSubscriptionPlanDto } from '../../services/subscriptionPlans.service';
import { useCurrency } from '../../hooks/useCurrency';
import { toast } from 'sonner';

interface CustomerTypesModalProps {
  onClose: () => void;
}

const EMPTY_FORM: CreateCustomerTypeDto & { id?: string } = {
  name: '',
  description: '',
  isSubscriptionBased: false,
  numberPrefix: '',
  paddingLength: 4,
};

const FREQ_LABELS: Record<string, string> = { MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', ANNUALLY: 'Annually' };

const EMPTY_PLAN_FORM: CreateSubscriptionPlanDto & { id?: string } = {
  name: '',
  description: '',
  price: undefined,
  defaultFrequency: undefined,
  isActive: true,
};

const INPUT = 'w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500';
const LABEL = 'block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5';

export function CustomerTypesModal({ onClose }: CustomerTypesModalProps) {
  const [types, setTypes] = useState<CustomerType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateCustomerTypeDto & { id?: string }>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Plans state
  const [expandedTypeId, setExpandedTypeId] = useState<string | null>(null);
  const [plansByType, setPlansByType] = useState<Record<string, SubscriptionPlan[]>>({});
  const [loadingPlans, setLoadingPlans] = useState<string | null>(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [planForm, setPlanForm] = useState<CreateSubscriptionPlanDto & { id?: string }>(EMPTY_PLAN_FORM);
  const [savingPlan, setSavingPlan] = useState(false);
  const [deletingPlanId, setDeletingPlanId] = useState<string | null>(null);
  const { formatCurrency } = useCurrency();

  const load = () => {
    setLoading(true);
    customerTypesService.getAll()
      .then((r) => { if (r.success && r.data) setTypes(r.data); })
      .catch(() => toast.error('Failed to load customer types'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (field: string, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const loadPlans = (typeId: string) => {
    setLoadingPlans(typeId);
    subscriptionPlansService.getByTypeId(typeId)
      .then((r) => { if (r.success && r.data) setPlansByType((prev) => ({ ...prev, [typeId]: r.data! })); })
      .catch(() => toast.error('Failed to load plans'))
      .finally(() => setLoadingPlans(null));
  };

  const toggleExpand = (typeId: string) => {
    if (expandedTypeId === typeId) {
      setExpandedTypeId(null);
      setShowPlanForm(false);
    } else {
      setExpandedTypeId(typeId);
      setShowPlanForm(false);
      if (!plansByType[typeId]) loadPlans(typeId);
    }
  };

  const openNewPlan = () => {
    setPlanForm(EMPTY_PLAN_FORM);
    setShowPlanForm(true);
  };

  const openEditPlan = (plan: SubscriptionPlan) => {
    setPlanForm({ id: plan.id, name: plan.name, description: plan.description || '', price: plan.price ? parseFloat(plan.price) : undefined, defaultFrequency: plan.defaultFrequency || undefined, isActive: plan.isActive });
    setShowPlanForm(true);
  };

  const setP = (field: string, value: any) => setPlanForm((f) => ({ ...f, [field]: value }));

  const handleSavePlan = async () => {
    if (!planForm.name.trim()) { toast.error('Plan name is required'); return; }
    if (!expandedTypeId) return;
    setSavingPlan(true);
    try {
      const dto = {
        name: planForm.name.trim(),
        description: planForm.description || undefined,
        price: planForm.price,
        defaultFrequency: (planForm.defaultFrequency as any) || undefined,
        isActive: planForm.isActive ?? true,
      };
      if (planForm.id) {
        const res = await subscriptionPlansService.update(planForm.id, dto);
        if (res.success && res.data) {
          toast.success('Plan updated');
          setPlansByType((prev) => ({ ...prev, [expandedTypeId]: prev[expandedTypeId].map((p) => p.id === planForm.id ? res.data! : p) }));
        }
      } else {
        const res = await subscriptionPlansService.create(expandedTypeId, dto);
        if (res.success && res.data) {
          toast.success('Plan created');
          setPlansByType((prev) => ({ ...prev, [expandedTypeId]: [...(prev[expandedTypeId] || []), res.data!] }));
        }
      }
      setShowPlanForm(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDeletePlan = async (plan: SubscriptionPlan) => {
    if (!expandedTypeId) return;
    setDeletingPlanId(plan.id);
    try {
      const res = await subscriptionPlansService.remove(plan.id);
      if (res.success) {
        toast.success('Plan deleted');
        setPlansByType((prev) => ({ ...prev, [expandedTypeId]: prev[expandedTypeId].filter((p) => p.id !== plan.id) }));
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete plan');
    } finally {
      setDeletingPlanId(null);
    }
  };

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (ct: CustomerType) => {
    setForm({
      id: ct.id,
      name: ct.name,
      description: ct.description || '',
      isSubscriptionBased: ct.isSubscriptionBased,
      numberPrefix: ct.numberPrefix,
      paddingLength: ct.paddingLength,
    });
    setEditingId(ct.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const dto = {
        name: form.name.trim(),
        description: form.description || undefined,
        isSubscriptionBased: form.isSubscriptionBased,
        numberPrefix: form.numberPrefix || '',
        paddingLength: form.paddingLength || 4,
      };
      if (editingId) {
        const res = await customerTypesService.update(editingId, dto);
        if (res.success) {
          toast.success('Customer type updated');
          setTypes((prev) => prev.map((t) => t.id === editingId ? { ...t, ...res.data! } : t));
        }
      } else {
        const res = await customerTypesService.create(dto);
        if (res.success && res.data) {
          toast.success('Customer type created');
          setTypes((prev) => [...prev, res.data!]);
        }
      }
      setShowForm(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ct: CustomerType) => {
    setDeletingId(ct.id);
    try {
      const res = await customerTypesService.remove(ct.id);
      if (res.success) {
        toast.success('Customer type deleted');
        setTypes((prev) => prev.filter((t) => t.id !== ct.id));
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  // Preview of the number format
  const preview = `${form.numberPrefix}${String(1).padStart(form.paddingLength || 4, '0')}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Customer Types</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Define the types of customers your business manages
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openNew}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add Type
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Inline Form */}
        {showForm && (
          <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              {editingId ? 'Edit Customer Type' : 'New Customer Type'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL}>Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className={INPUT}
                  placeholder="e.g. Subscriber, VIP, Corporate"
                />
              </div>
              <div>
                <label className={LABEL}>Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  className={INPUT}
                  placeholder="Optional description"
                />
              </div>
              <div>
                <label className={LABEL}>Number Prefix</label>
                <input
                  type="text"
                  value={form.numberPrefix}
                  onChange={(e) => set('numberPrefix', e.target.value.toUpperCase())}
                  className={INPUT}
                  placeholder="e.g. SUB- VIP- CL-"
                  maxLength={10}
                />
              </div>
              <div>
                <label className={LABEL}>Padding Length</label>
                <input
                  type="number"
                  min={1}
                  max={8}
                  value={form.paddingLength}
                  onChange={(e) => set('paddingLength', parseInt(e.target.value) || 4)}
                  className={INPUT}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isSubscriptionBased}
                      onChange={(e) => set('isSubscriptionBased', e.target.checked)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                      <RefreshCw className="w-3.5 h-3.5 text-indigo-500" /> Subscription-based
                    </span>
                  </label>
                  {form.numberPrefix !== undefined && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ml-4">
                      <Hash className="w-3 h-3" />
                      Preview: <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400">{preview}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowForm(false)}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60"
                  >
                    {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {editingId ? 'Save Changes' : 'Create Type'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* List */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
            </div>
          ) : types.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-6">
              <div className="w-14 h-14 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">No customer types yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Create customer types to categorise and number your customers automatically.
              </p>
              <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
                <Plus className="w-4 h-4" /> Add first type
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  {['Name', 'Number Series', 'Subscription', 'Customers', ''].map((h) => (
                    <th key={h} className="text-left px-6 py-3">
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{h}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {types.map((ct) => {
                  const isExpanded = expandedTypeId === ct.id;
                  const plans = plansByType[ct.id] || [];
                  return (
                    <>
                    <tr key={ct.id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{ct.name}</p>
                        {ct.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ct.description}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 text-xs font-mono bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-lg">
                          <Hash className="w-3 h-3" />
                          {ct.numberPrefix || '—'}{String(ct.nextNumber).padStart(ct.paddingLength, '0')}
                          <span className="text-gray-400 ml-1">(next)</span>
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {ct.isSubscriptionBased ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 rounded-full text-xs font-medium">
                            <RefreshCw className="w-3 h-3" /> Yes
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {ct._count?.customers ?? 0}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 justify-end">
                          {ct.isSubscriptionBased && (
                            <button
                              onClick={() => toggleExpand(ct.id)}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                              title="Manage plans"
                            >
                              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                              Plans                              {(ct._count?.plans ?? 0) > 0 && (
                                <span className="ml-0.5 px-1.5 py-0.5 bg-indigo-600 text-white rounded-full text-[10px] font-semibold leading-none">
                                  {ct._count!.plans}
                                </span>
                              )}                            </button>
                          )}
                          <button
                            onClick={() => openEdit(ct)}
                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                          </button>
                          <button
                            onClick={() => handleDelete(ct)}
                            disabled={deletingId === ct.id || (ct._count?.customers ?? 0) > 0}
                            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={(ct._count?.customers ?? 0) > 0 ? 'Cannot delete — has customers' : 'Delete'}
                          >
                            {deletingId === ct.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin text-red-500" />
                              : <Trash2 className="w-3.5 h-3.5 text-red-400 dark:text-red-500" />
                            }
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${ct.id}-plans`} className="bg-indigo-50/50 dark:bg-indigo-900/10">
                        <td colSpan={5} className="px-6 py-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-400 uppercase tracking-wide">Subscription Plans</span>
                            <button
                              onClick={openNewPlan}
                              className="flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                              <Plus className="w-3 h-3" /> Add Plan
                            </button>
                          </div>

                          {/* Plan form */}
                          {showPlanForm && (
                            <div className="bg-white dark:bg-gray-800 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4 mb-3">
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className={LABEL}>Plan Name *</label>
                                  <input type="text" value={planForm.name} onChange={(e) => setP('name', e.target.value)} className={INPUT} placeholder="e.g. Monthly Basic, Annual Pro" />
                                </div>
                                <div>
                                  <label className={LABEL}>Price</label>
                                  <input type="number" min={0} step={0.01} value={planForm.price ?? ''} onChange={(e) => setP('price', e.target.value ? parseFloat(e.target.value) : undefined)} className={INPUT} placeholder="0.00" />
                                </div>
                                <div>
                                  <label className={LABEL}>Default Frequency</label>
                                  <select value={planForm.defaultFrequency ?? ''} onChange={(e) => setP('defaultFrequency', e.target.value || undefined)} className={INPUT}>
                                    <option value="">— None —</option>
                                    <option value="MONTHLY">Monthly</option>
                                    <option value="QUARTERLY">Quarterly</option>
                                    <option value="ANNUALLY">Annually</option>
                                  </select>
                                </div>
                                <div>
                                  <label className={LABEL}>Description</label>
                                  <input type="text" value={planForm.description} onChange={(e) => setP('description', e.target.value)} className={INPUT} placeholder="Optional" />
                                </div>
                              </div>
                              <div className="flex items-center justify-between">
                                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                                  <input type="checkbox" checked={planForm.isActive ?? true} onChange={(e) => setP('isActive', e.target.checked)} className="w-4 h-4 text-indigo-600 border-gray-300 rounded" />
                                  Active
                                </label>
                                <div className="flex gap-2">
                                  <button onClick={() => setShowPlanForm(false)} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">Cancel</button>
                                  <button onClick={handleSavePlan} disabled={savingPlan} className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors">
                                    {savingPlan && <Loader2 className="w-3 h-3 animate-spin" />}
                                    {planForm.id ? 'Save' : 'Create'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Plans list */}
                          {loadingPlans === ct.id ? (
                            <div className="flex items-center gap-2 py-3 text-xs text-gray-500 dark:text-gray-400">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading plans...
                            </div>
                          ) : plans.length === 0 ? (
                            <p className="text-xs text-gray-400 dark:text-gray-500 italic py-2">No plans yet. Add one above.</p>
                          ) : (
                            <div className="space-y-1.5">
                              {plans.map((plan) => (
                                <div key={plan.id} className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg px-3 py-2">
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{plan.name}</span>
                                    {plan.price && (
                                      <span className="inline-flex items-center gap-0.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full font-medium">
                                        {formatCurrency(plan.price)}
                                      </span>
                                    )}
                                    {plan.defaultFrequency && (
                                      <span className="inline-flex items-center text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-2 py-0.5 rounded-full font-medium">
                                        {FREQ_LABELS[plan.defaultFrequency]}
                                      </span>
                                    )}
                                    {!plan.isActive && (
                                      <span className="text-xs text-gray-400 dark:text-gray-500 italic">inactive</span>
                                    )}
                                    {plan.description && (
                                      <span className="text-xs text-gray-400 dark:text-gray-500">{plan.description}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-400 dark:text-gray-500 mr-2">{plan._count?.customers ?? 0} customers</span>
                                    <button onClick={() => openEditPlan(plan)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                                      <Pencil className="w-3 h-3 text-gray-400" />
                                    </button>
                                    <button
                                      onClick={() => handleDeletePlan(plan)}
                                      disabled={deletingPlanId === plan.id || (plan._count?.customers ?? 0) > 0}
                                      title={(plan._count?.customers ?? 0) > 0 ? 'Cannot delete — has customers' : 'Delete'}
                                      className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      {deletingPlanId === plan.id
                                        ? <Loader2 className="w-3 h-3 animate-spin text-red-500" />
                                        : <Trash2 className="w-3 h-3 text-red-400" />
                                      }
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
